import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar,
  MapPin,
  ScanLine,
  Users,
  CheckCircle2,
  Search,
  Loader2,
  ArrowLeft,
  Ticket,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BACKEND_URL } from '../lib/api';

/**
 * Per-event associate tools page — gated by GET /api/associate/events/{id}/access.
 * Renders:
 *   - Event header + permissions chip row
 *   - Tools panel (scanner deep-link, guest list, check-in summary)
 *   - Searchable guest list with optional manual check-in
 *
 * Backend enforces all writes; this page is just a polished UI layer.
 */
const AssociateToolsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [access, setAccess] = useState(null); // { event, permissions, role }
  const [guests, setGuests] = useState([]);
  const [guestsLoading, setGuestsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [checkingIn, setCheckingIn] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/associate/events/${encodeURIComponent(eventId)}/access`,
          { withCredentials: true },
        );
        if (active) setAccess(res.data);
      } catch (e) {
        if (!active) return;
        // 403 (no access) and 404 (event deleted/missing) both surface as
        // "no access" so users never land on a blank page from a stale link.
        if (e.response?.status === 403 || e.response?.status === 404) {
          setDenied(true);
        } else {
          setErrorMsg(e.response?.data?.detail || 'Could not load this event.');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => {
    if (!access?.permissions?.can_view_guest_list) return;
    let active = true;
    setGuestsLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/organiser/events/${encodeURIComponent(eventId)}/guests`,
          { withCredentials: true },
        );
        if (active) setGuests(res.data?.guests || []);
      } catch {
        if (active) setGuests([]);
      } finally {
        if (active) setGuestsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [eventId, access?.permissions?.can_view_guest_list]);

  const manualCheckIn = async (ticketCode) => {
    setCheckingIn(ticketCode);
    try {
      await axios.post(
        `${BACKEND_URL}/api/tickets/validate`,
        { qr_data: ticketCode, event_id: eventId },
        { withCredentials: true },
      );
      toast.success('Checked in successfully.');
      setGuests((prev) =>
        prev.map((g) => (g.ticket_code === ticketCode ? { ...g, checked_in: true } : g))
      );
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not check in this ticket.';
      toast.error(msg);
    } finally {
      setCheckingIn(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F3D2B]" />
      </div>
    );
  }

  if (denied) {
    return (
      <div className="min-h-[80vh] bg-stone-50 py-16 px-4">
        <div className="max-w-md mx-auto bg-white border border-stone-200 rounded-2xl shadow-sm px-8 py-10 text-center" data-testid="associate-tools-denied">
          <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
            <Ticket className="w-6 h-6 text-rose-500" />
          </div>
          <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2">Access denied</h1>
          <p className="text-stone-600 mb-6">You do not have access to this event.</p>
          <Link
            to="/associate/events"
            className="inline-block bg-[#1F3D2B] text-white px-6 py-3 rounded-full font-medium"
          >
            View my events
          </Link>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-[80vh] bg-stone-50 flex items-center justify-center px-4">
        <p className="text-stone-600">{errorMsg}</p>
      </div>
    );
  }

  const { event, permissions } = access || {};
  const totalCheckedIn = guests.filter((g) => g.checked_in).length;
  const totalGuests = guests.length;
  const filtered = guests.filter((g) => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return true;
    return (
      (g.guest_name || '').toLowerCase().includes(q) ||
      (g.guest_email || '').toLowerCase().includes(q) ||
      (g.ticket_code || '').toLowerCase().includes(q)
    );
  });

  // Hard route guard fallback — if the route ever resolves without an event_id
  if (!event?.event_id) {
    return <Navigate to="/associate/events" replace />;
  }

  return (
    <div className="min-h-[80vh] bg-stone-50 py-10 px-4">
      <div className="max-w-4xl mx-auto" data-testid="associate-tools-page">
        <button
          type="button"
          onClick={() => navigate('/associate/events')}
          className="text-xs text-[#1F3D2B] hover:underline mb-4 flex items-center gap-1"
          data-testid="associate-tools-back"
        >
          <ArrowLeft className="w-3 h-3" /> All my events
        </button>

        {/* Event header */}
        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-6 mb-6" data-testid="associate-tools-header">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A27A] mb-1">Event Tools</p>
          <h1 className="font-serif text-3xl text-[#1F3D2B] mb-3" data-testid="associate-tools-event-title">
            {event?.title}
          </h1>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#4A5D50]">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {event?.date}{event?.time ? ` · ${event.time}` : ''}
            </span>
            {event?.location && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                {event.location}
              </span>
            )}
          </div>
        </div>

        {/* Stats row */}
        {permissions?.can_view_guest_list && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6" data-testid="associate-tools-stats">
            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <p className="text-xs text-[#4A5D50]">Tickets sold</p>
              <p className="font-serif text-2xl text-[#1F3D2B]">{totalGuests}</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <p className="text-xs text-[#4A5D50]">Checked in</p>
              <p className="font-serif text-2xl text-emerald-700" data-testid="associate-tools-checkin-count">
                {totalCheckedIn}
              </p>
            </div>
            <div className="bg-white border border-stone-200 rounded-xl p-4 col-span-2 md:col-span-1">
              <p className="text-xs text-[#4A5D50]">Awaiting</p>
              <p className="font-serif text-2xl text-amber-700">{Math.max(0, totalGuests - totalCheckedIn)}</p>
            </div>
          </div>
        )}

        {/* Tools grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {permissions?.can_scan_tickets && (
            <Link
              to={`/associate/events/${eventId}/scanner`}
              className="bg-[#1F3D2B] text-white rounded-2xl p-5 hover:bg-[#172E20] transition-colors flex items-center gap-3"
              data-testid="associate-tools-open-scanner"
            >
              <ScanLine className="w-6 h-6 text-[#C9A27A]" />
              <div>
                <p className="font-serif text-lg">Scan tickets</p>
                <p className="text-xs text-white/70">Open the door-scanner camera.</p>
              </div>
            </Link>
          )}
          {permissions?.can_view_guest_list && (
            <a
              href="#guest-list"
              className="bg-white border border-stone-200 rounded-2xl p-5 hover:bg-stone-50 transition-colors flex items-center gap-3"
              data-testid="associate-tools-jump-guests"
            >
              <Users className="w-6 h-6 text-[#1F3D2B]" />
              <div>
                <p className="font-serif text-lg text-[#1F3D2B]">Guest list</p>
                <p className="text-xs text-[#4A5D50]">Search and check-in manually.</p>
              </div>
            </a>
          )}
        </div>

        {/* Guest list */}
        {permissions?.can_view_guest_list && (
          <div id="guest-list" className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden" data-testid="associate-tools-guest-list">
            <div className="p-5 border-b border-stone-100 flex items-center gap-3 flex-wrap">
              <h2 className="font-serif text-xl text-[#1F3D2B]">Guests</h2>
              <span className="text-xs text-[#4A5D50]">
                {totalCheckedIn} / {totalGuests} checked in
              </span>
              <div className="ml-auto relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5D50]" />
                <Input
                  placeholder="Search name, email, or code…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="associate-tools-search"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              {guestsLoading ? (
                <p className="p-6 text-center text-sm text-[#4A5D50]">Loading guests…</p>
              ) : filtered.length === 0 ? (
                <p className="p-6 text-center text-sm text-[#4A5D50]">No guests found.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-[#4A5D50]">
                    <tr>
                      <th className="text-left px-4 py-2">Guest</th>
                      <th className="text-left px-4 py-2 hidden md:table-cell">Email</th>
                      <th className="text-left px-4 py-2 hidden sm:table-cell">Ticket</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-right px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((g) => (
                      <tr key={g.ticket_code} className="border-t border-stone-100" data-testid={`associate-tools-guest-${g.ticket_code}`}>
                        <td className="px-4 py-2 text-[#112318]">
                          {g.guest_name}
                          {g.is_comp && (
                            <span className="ml-2 text-[10px] uppercase tracking-widest bg-[#C9A27A]/30 text-[#9C7434] px-1.5 py-0.5 rounded">Comp</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-[#4A5D50] hidden md:table-cell truncate">{g.guest_email || ''}</td>
                        <td className="px-4 py-2 text-[#4A5D50] hidden sm:table-cell font-mono text-xs">{g.ticket_code}</td>
                        <td className="px-4 py-2">
                          {g.cancelled ? (
                            <span className="text-[10px] uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">Cancelled</span>
                          ) : g.checked_in ? (
                            <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Checked in
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Awaiting</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {!g.cancelled && !g.checked_in && permissions?.can_check_in_guests && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => manualCheckIn(g.ticket_code)}
                              disabled={checkingIn === g.ticket_code}
                              className="rounded-full"
                              data-testid={`associate-tools-checkin-${g.ticket_code}`}
                            >
                              {checkingIn === g.ticket_code ? 'Checking…' : 'Check in'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssociateToolsPage;
